import React, { useEffect, useMemo } from 'react';
import { useReactiveVar } from '@apollo/client';
import { Flex, Grid } from '@mantine/core';

import useDesktop from '@/hooks/useDesktop';
import { HEADER_HEIGHT } from '@/constants/variables';

import { MenusPageHeader } from './Menus.page.header';
import { MenusRestaurantListSection } from './MenusRestaurantList.section/MenusRestaurantList.section';
import { MenuDetailsSection } from './MenuDetails.section/MenuDetails.section';
import { MenuDetailsEmpty } from './MenuDetails.section/MenuDetails.empty';
import { useMenuRestaurants } from './hooks/useMenuRestaurants';
import { useVendorMenuTemplates } from './hooks/useVendorMenuTemplates';
import { filterTemplates, resolveSelectedTemplateId, resolveSelectedVendorId } from './helpers';
import { DEFAULT_FILTERS } from './constants';
import { filtersVar, selectedTemplateIdVar, selectedVendorIdVar } from './state';

export const MenusPage: React.FC = () => {
  const isDesktop = useDesktop();

  const { restaurants, loading: restaurantsLoading } = useMenuRestaurants();

  const selectedVendorId = useReactiveVar(selectedVendorIdVar);
  const selectedTemplateId = useReactiveVar(selectedTemplateIdVar);
  const filters = useReactiveVar(filtersVar);

  useEffect(() => {
    const nextVendorId = resolveSelectedVendorId(restaurants, selectedVendorId);
    if (nextVendorId !== selectedVendorId) {
      selectedVendorIdVar(nextVendorId);
    }
  }, [restaurants, selectedVendorId]);

  const { templates, loading, error } = useVendorMenuTemplates(selectedVendorId);

  const filteredTemplates = useMemo(
    () => filterTemplates(templates, filters),
    [templates, filters]
  );

  useEffect(() => {
    const nextTemplateId = resolveSelectedTemplateId(filteredTemplates, selectedTemplateId);
    if (nextTemplateId !== selectedTemplateId) {
      selectedTemplateIdVar(nextTemplateId);
    }
  }, [filteredTemplates, selectedTemplateId]);

  const handleVendorChange = (vendorId: string) => {
    selectedVendorIdVar(vendorId);
    filtersVar(DEFAULT_FILTERS);
    selectedTemplateIdVar(null);
  };

  return (
    <Flex direction="column" h={isDesktop ? `calc(100vh - ${HEADER_HEIGHT}px)` : undefined}>
      <MenusPageHeader
        restaurants={restaurants}
        selectedVendorId={selectedVendorId}
        onVendorChange={handleVendorChange}
        productTypes={filters.productTypes}
        onProductTypesChange={(productTypes) => filtersVar({ ...filtersVar(), productTypes })}
      />

      <Grid
        gutter={0}
        flex={isDesktop ? 1 : undefined}
        mih={isDesktop ? 0 : undefined}
        styles={isDesktop ? { inner: { height: '100%' } } : undefined}
      >
        <Grid.Col
          span={isDesktop ? 4 : 12}
          h={isDesktop ? '100%' : undefined}
          style={
            isDesktop
              ? { borderRight: '1px solid var(--mantine-color-gray-3)', overflow: 'hidden' }
              : undefined
          }
        >
          <MenusRestaurantListSection
            templates={filteredTemplates}
            loading={loading}
            error={error}
            filters={filters}
            onFiltersChange={filtersVar}
            selectedTemplateId={selectedTemplateId}
            onSelectTemplate={selectedTemplateIdVar}
          />
        </Grid.Col>
        <Grid.Col
          span={isDesktop ? 8 : 12}
          h={isDesktop ? '100%' : undefined}
          style={isDesktop ? { overflow: 'auto' } : undefined}
        >
          {selectedVendorId && selectedTemplateId ? (
            <MenuDetailsSection templateId={selectedTemplateId} />
          ) : (
            <MenuDetailsEmpty
              hasRestaurants={restaurants.length > 0}
              hasTemplates={templates.length > 0}
              isLoading={loading || restaurantsLoading}
            />
          )}
        </Grid.Col>
      </Grid>
    </Flex>
  );
};

